import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import "./Footer.css";
import { ENDPOINTS, apiFetch } from "../api/api";
import useFetch from "../hooks/useFetch";
import { useAuth } from "../auth/AuthContext";
import { useTranslation } from "react-i18next";
import "../i18n";

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const year = new Date().getFullYear();
  const { user } = useAuth();

  const { data: footerLinksData } = useFetch(ENDPOINTS.footerLinks);
  const { data: socialLinksData } = useFetch(ENDPOINTS.socialLinks);
  const allFooterLinks = footerLinksData || [];
  const quickLinks   = allFooterLinks.filter((l) => l.section === "quick");
  const serviceLinks = allFooterLinks.filter((l) => l.section === "services");
  const socialLinks  = socialLinksData || [];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) { setStatus("error"); return; }
    setLoading(true);
    try {
      await apiFetch(ENDPOINTS.contact_messages, {
        method: "POST",
        body: JSON.stringify({ name: "Newsletter", email, message: "Newsletter subscription", subject: "Newsletter" }),
      });
      setStatus("success"); setEmail("");
    } catch { setStatus("success"); setEmail(""); }
    finally { setLoading(false); }
  };

  return (
    <footer className="ft-footer">
      <div className="ft-top-bar"></div>
      <div className="ft-bg">
        <div className="ft-orb ft-orb--1"></div>
        <div className="ft-orb ft-orb--2"></div>
        <div className="ft-grid"></div>
      </div>
      <div className="ft-container">

        <div className="ft-newsletter">
          <div className="ft-newsletter-orb"></div>
          <div className="ft-newsletter-text">
            <span className="ft-newsletter-eyebrow">
              <i className="fa-solid fa-bell"></i>{t("footer.newsletter.eyebrow")}
            </span>
            <h3 className="ft-newsletter-title">{t("footer.newsletter.title")}</h3>
            <p className="ft-newsletter-desc">{t("footer.newsletter.desc")}</p>
          </div>
          <form className="ft-newsletter-form" onSubmit={handleSubscribe} noValidate>
            <div className="ft-newsletter-input-wrap">
              <i className="fa-solid fa-envelope ft-newsletter-icon"></i>
              <input type="email" className="ft-newsletter-input"
                placeholder={t("footer.newsletter.placeholder")}
                value={email} onChange={(e) => { setEmail(e.target.value); setStatus(null); }}
                disabled={loading || status === "success"} />
            </div>
            <button type="submit" className="ft-newsletter-btn" disabled={loading || status === "success"}>
              {loading ? <span className="ft-newsletter-spinner"></span>
                : status === "success" ? <><i className="fa-solid fa-check"></i> {t("footer.newsletter.subscribed")}</>
                : <><i className="fa-solid fa-paper-plane"></i> {t("footer.newsletter.btn")}</>}
            </button>
          </form>
          {status === "error" && <p className="ft-newsletter-error"><i className="fa-solid fa-triangle-exclamation"></i> {t("footer.newsletter.error")}</p>}
          {status === "success" && <p className="ft-newsletter-success"><i className="fa-solid fa-circle-check"></i> {t("footer.newsletter.success")}</p>}
        </div>

        <div className="ft-divider"></div>

        <div className="ft-main">
          <div className="ft-brand">
            <NavLink to="/" className="ft-brand-name">SolvioTech</NavLink>
            <p className="ft-brand-desc">{t("footer.desc")}</p>
            <div className="ft-socials">
              {socialLinks.map((s) => (
                <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer"
                  className="ft-social-btn" aria-label={s.label} style={{ "--hover-color": s.color }}>
                  <i className={s.icon}></i>
                </a>
              ))}
            </div>
            <div className="ft-status"><span className="ft-status-dot"></span>{t("footer.available")}</div>
          </div>

          <div className="ft-col">
            <h4 className="ft-col-title">{t("footer.quickLinks")}</h4>
            <ul className="ft-link-list">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <NavLink to={link.to} className="ft-link">
                    <i className="fa-solid fa-chevron-right ft-link-arrow"></i>{link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="ft-col">
            <h4 className="ft-col-title">{t("footer.services")}</h4>
            <ul className="ft-link-list">
              {serviceLinks.map((link) => (
                <li key={link.id}>
                  <NavLink to="/services" className="ft-link">
                    <i className="fa-solid fa-chevron-right ft-link-arrow"></i>{link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="ft-col">
            <h4 className="ft-col-title">{t("footer.contact")}</h4>
            <div className="ft-contact-list">
              <a href="mailto:hello@solviotech.com" className="ft-contact-item">
                <div className="ft-contact-icon"><i className="fa-solid fa-envelope"></i></div>
                <div className="ft-contact-text">
                  <span className="ft-contact-label">Email</span>
                  <span className="ft-contact-value">hello@solviotech.com</span>
                </div>
              </a>
              <a href="tel:+254720955034" className="ft-contact-item">
                <div className="ft-contact-icon"><i className="fa-solid fa-phone"></i></div>
                <div className="ft-contact-text">
                  <span className="ft-contact-label">Phone</span>
                  <span className="ft-contact-value">+254 720 955 034 <br /> +254 757 913 737</span>
                </div>
              </a>
              <div className="ft-contact-item">
                <div className="ft-contact-icon"><i className="fa-solid fa-location-dot"></i></div>
                <div className="ft-contact-text">
                  <span className="ft-contact-label">Location</span>
                  <span className="ft-contact-value">Nairobi, Kenya 🇰🇪</span>
                </div>
              </div>
            </div>
            <NavLink to="/contact" className="ft-cta-btn">
              <i className="fa-solid fa-bolt"></i>{t("footer.letsTalk")}
            </NavLink>
          </div>
        </div>

        <div className="ft-divider ft-divider--bottom"></div>
        <div className="ft-bottom">
          <p className="ft-copyright">© {year} Solviotech. {t("footer.rights")}</p>
          <div className="ft-bottom-links">
            <a href="/privacy" className="ft-bottom-link">{t("footer.privacy")}</a>
            <span className="ft-bottom-sep">·</span>
            <a href="/terms" className="ft-bottom-link">{t("footer.terms")}</a>
            <span className="ft-bottom-sep">·</span>
            {user ? (
              <Link to="/admin/dashboard" className="ft-bottom-link" style={{ opacity: 0.6 }}>
                <i className="fa-solid fa-circle-user" style={{ marginRight: "0.3rem" }}></i>
                {t("footer.welcome")}, {user.username}
              </Link>
            ) : (
              <Link to="/login" className="ft-bottom-link" style={{ opacity: 0.4 }}>{t("footer.signIn")}</Link>
            )}
          </div>
          <p className="ft-credit">
            {t("footer.builtBy")} <span className="ft-heart">❤</span> by{" "}
            <span className="ft-author">Joseph Kennedy (JK)</span>
            {" "}&amp;{" "}
            <span className="ft-author">Philip Osir</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
